/**
 * IntegrityClient - communicates with /api/adr/script-integrity
 *
 * Payload: script_url (required), script_hash (required for first_party; null for third_party).
 * Optional: normalized_script_url, source, can_hash, hash_status .
 *
 * reportBatch() sends the scripts of a scan in one request, {"scripts": [...]}, at most
 * BATCH_MAX per request. One request per script was about 46 on the first page a tab opens,
 * and the portal limits a sensor token and an IP to 60 a minute: a second new tab within the
 * minute got 429s, and the scripts they carried went unchecked on that page (Task 9518).
 * A portal older than the batch refuses the shape; the client then sends one script per
 * request, as before, for the rest of the page.
 */
export const BATCH_MAX = 50;

/** Statuses a portal without the batch answers a batch with: send one script at a time. */
const BATCH_UNSUPPORTED = [400, 404, 405, 413, 422];

export class IntegrityClient {
	constructor(config, logger, options = {}) {
		this.config = config;
		this.logger = logger;
		/** @type {import('../auth/session-token-manager.js').SessionTokenManager|null} */
		this.tokenManager = options.tokenManager || null;
		/** @type {import('../core/sensor-integrity.js').SensorIntegrity|null} */
		this.sensorIntegrity = options.sensorIntegrity || null;
	}

	getEndpoint() {
		return this.config?.get?.('scriptIntegrityEndpoint') || '/api/adr/script-integrity';
	}

	async _getSensorHash() {
		try {
			if (this.sensorIntegrity && typeof this.sensorIntegrity.getSensorHash === 'function') {
				return await this.sensorIntegrity.getSensorHash();
			}
		} catch {
		}
		return (this.config?.get?.('sensorHash')) || null;
	}

	async _makeRequest(body, options = {}) {
		const headers = {
			'Content-Type': 'application/json',
			'Accept': 'application/json'
		};

		if (this.tokenManager) {
			const jwt = options && options._forceFreshToken
				? await this.tokenManager.forceRefresh()
				: await this.tokenManager.getToken();
			headers['Authorization'] = `Bearer ${jwt}`;
		}

		const sensorHash = await this._getSensorHash();
		if (sensorHash) {
			headers['X-Sensor-Integrity'] = sensorHash;
		}

		const timeout = Number(this.config?.get?.('timeout')) || 15000;
		const controller = new AbortController();
		const timeoutId = setTimeout(() => controller.abort(), timeout);

		try {
			const response = await fetch(this.getEndpoint(), {
				method: 'POST',
				headers,
				body: JSON.stringify(body),
				signal: controller.signal
			});
			clearTimeout(timeoutId);
			return response;
		} catch (error) {
			clearTimeout(timeoutId);
			if (error.name === 'AbortError') {
				throw new Error('Script integrity request timeout');
			}
			throw error;
		}
	}

	/** The request body for one script, or null when the script cannot be reported. */
	_buildBody(payload) {
		const { script_url, script_hash, normalized_script_url, source, can_hash, hash_status } = payload ?? {};
		if (!script_url) return null;
		const isThirdParty = source === 'third_party';
		if (!isThirdParty && script_hash == null) return null;

		const body = {
			script_url,
			script_hash: script_hash ?? null
		};
		if (normalized_script_url !== undefined) body.normalized_script_url = normalized_script_url;
		if (source !== undefined) body.source = source;
		if (can_hash !== undefined) body.can_hash = can_hash;
		if (hash_status !== undefined && script_hash == null) body.hash_status = hash_status;
		return body;
	}

	/** One POST, retried once with a fresh token on 401/403. { ok, status, json }, or null on a network error. */
	async _post(body) {
		try {
			let res = await this._makeRequest(body);

			if ((res.status === 401 || res.status === 403) && this.tokenManager) {
				try {
					await this.tokenManager.forceRefresh();
					res = await this._makeRequest(body, { _forceFreshToken: false });
				} catch {
				}
			}

			const json = await res.json().catch(() => null);
			return { ok: res.ok, status: res.status, json };
		} catch (err) {
			this.logger?.warn?.('Script integrity report error', err);
			return null;
		}
	}

	async _sendOne(body) {
		const res = await this._post(body);
		if (!res) return null;
		if (!res.ok) {
			this.logger?.warn?.('Script integrity report failed', { status: res.status, body: res.json });
			return null;
		}
		return res.json;
	}

	async report(payload) {
		const body = this._buildBody(payload);
		if (!body) return null;
		return this._sendOne(body);
	}

	/**
	 * Reports several scripts; resolves to one result per payload, in order: the portal's answer
	 * for that script, or null when it could not be reported (the caller may try it again later).
	 */
	async reportBatch(payloads) {
		const list = Array.isArray(payloads) ? payloads : [];
		const results = new Array(list.length).fill(null);
		const bodies = list.map((p) => this._buildBody(p));
		const pending = bodies.map((b, i) => (b ? i : -1)).filter((i) => i >= 0);

		for (let start = 0; start < pending.length; start += BATCH_MAX) {
			const chunk = pending.slice(start, start + BATCH_MAX);

			if (this._batchUnsupported) {
				for (const i of chunk) results[i] = await this._sendOne(bodies[i]);
				continue;
			}

			const res = await this._post({ scripts: chunk.map((i) => bodies[i]) });
			const answered = res && res.ok && Array.isArray(res.json?.results) && res.json.results.length === chunk.length;
			if (answered) {
				chunk.forEach((i, k) => {
					const item = res.json.results[k];
					// A script the portal could not check carries its error; a single report got a 400.
					results[i] = item && !item.error ? item : null;
				});
				continue;
			}

			if (res && BATCH_UNSUPPORTED.includes(res.status)) {
				this.logger?.debug?.('Script integrity batch not supported; sending one script per request', { status: res.status });
				this._batchUnsupported = true;
				for (const i of chunk) results[i] = await this._sendOne(bodies[i]);
				continue;
			}

			this.logger?.warn?.('Script integrity batch failed', { status: res?.status ?? null });
		}
		return results;
	}
}

export default IntegrityClient;

